import { lazy, Suspense, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { Rol } from "@zociety/core";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TemaProvider } from "./contexts/TemaContext";
import { CarritoProvider } from "./contexts/CarritoContext";
import Layout from "./components/Layout";
import Cargando from "./components/Cargando";

const Landing = lazy(() => import("./pages/Landing"));
const Login = lazy(() => import("./pages/Login"));
const Accion = lazy(() => import("./pages/Accion"));
const AvisoPrivacidad = lazy(() => import("./pages/AvisoPrivacidad"));
const Panel = lazy(() => import("./pages/Panel"));
const Tienda = lazy(() => import("./pages/Tienda"));
const Reservar = lazy(() => import("./pages/Reservar"));
const Perfil = lazy(() => import("./pages/Perfil"));
const Coaches = lazy(() => import("./pages/Coaches"));
const Instalaciones = lazy(() => import("./pages/Instalaciones"));
const Admin = lazy(() => import("./pages/admin/Admin"));

/** Puerta de acceso: sesión, estado de cuenta y rol. */
function Protegida({ children, roles }: { children: ReactNode; roles?: readonly Rol[] }) {
  const { cuenta, perfil, rol, cargando } = useAuth();
  const ubicacion = useLocation();

  if (cargando) return <Cargando pantalla />;
  if (!cuenta) return <Navigate to="/entrar" state={{ desde: ubicacion.pathname }} replace />;
  if (!perfil) return <Cargando pantalla mensaje="Preparando tu perfil…" />;

  if (perfil.estado === "inactivo") {
    return (
      <div className="contenedor centro-pantalla">
        <div className="tarjeta" style={{ maxWidth: 460, textAlign: "center" }}>
          <h2 className="degradado">Cuenta inactiva</h2>
          <p className="suave" style={{ marginTop: "var(--e3)" }}>
            Tu cuenta está desactivada. Acércate a recepción o escríbenos para reactivarla.
          </p>
        </div>
      </div>
    );
  }

  if (roles && (!rol || !roles.includes(rol))) return <Navigate to="/panel" replace />;

  return <>{children}</>;
}

/** Manda a cada quien a su portal según su rol. */
function Repartidor() {
  const { cuenta, rol, cargando } = useAuth();
  if (cargando) return <Cargando pantalla />;
  if (!cuenta) return <Navigate to="/entrar" replace />;
  if (rol && rol !== "alumno") return <Navigate to="/admin" replace />;
  return <Navigate to="/panel" replace />;
}

export default function App() {
  return (
    <TemaProvider>
      <AuthProvider>
        <CarritoProvider>
          <BrowserRouter>
            <a className="salto-contenido" href="#contenido">
              Saltar al contenido
            </a>
            <Suspense fallback={<Cargando pantalla />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                {/*
                  Tres rutas para la misma pantalla. El modo se deduce de la
                  ruta, así que la barra de direcciones dice en cuál estás, el
                  registro se puede enlazar desde una campaña y el botón atrás
                  se comporta como la persona espera.
                */}
                <Route path="/entrar" element={<Login />} />
                <Route path="/crear-cuenta" element={<Login />} />
                <Route path="/recuperar" element={<Login />} />
                {/*
                  Adonde llevan los correos de acceso. Es pública a propósito:
                  quien restablece su contraseña no tiene sesión, y quien
                  confirma su correo puede abrir el enlace en otro dispositivo.
                */}
                <Route path="/accion" element={<Accion />} />
                <Route path="/ir" element={<Repartidor />} />
                {/*
                  Pública y sin envoltorio de sesión, y eso es un requisito
                  legal, no una comodidad: la ley obliga a ponerlo a disposición
                  del titular **antes** de recabar sus datos, así que tiene que
                  poder leerse desde la pantalla de registro, sin cuenta.
                */}
                <Route
                  path="/aviso-de-privacidad"
                  element={
                    <div className="contenedor">
                      <AvisoPrivacidad />
                    </div>
                  }
                />

                <Route
                  path="/panel"
                  element={
                    <Protegida>
                      <Layout>
                        <Panel />
                      </Layout>
                    </Protegida>
                  }
                />
                <Route
                  path="/tienda"
                  element={
                    <Protegida>
                      <Layout>
                        <Tienda />
                      </Layout>
                    </Protegida>
                  }
                />
                <Route
                  path="/reservar"
                  element={
                    <Protegida>
                      <Layout>
                        <Reservar />
                      </Layout>
                    </Protegida>
                  }
                />
                <Route
                  path="/coaches"
                  element={
                    <Protegida>
                      <Layout>
                        <Coaches />
                      </Layout>
                    </Protegida>
                  }
                />
                <Route
                  path="/instalaciones"
                  element={
                    <Protegida>
                      <Layout>
                        <Instalaciones />
                      </Layout>
                    </Protegida>
                  }
                />
                <Route
                  path="/perfil"
                  element={
                    <Protegida>
                      <Layout>
                        <Perfil />
                      </Layout>
                    </Protegida>
                  }
                />

                <Route
                  path="/admin/*"
                  element={
                    <Protegida roles={["admin", "recepcion", "coach"]}>
                      <Admin />
                    </Protegida>
                  }
                />

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </CarritoProvider>
      </AuthProvider>
    </TemaProvider>
  );
}
